/**
 * Pure decision logic for Bug #758 ConnectionsRoleGate.
 *
 * Extracted so we can regress-test the gate without standing up React
 * + the auth-context provider. The wrapper component delegates the
 * branching to this function and only renders the placeholder /
 * children.
 *
 * ─── Foundations 1 / B1-T3: the ladder is gone ────────────────────────────
 *
 * This file carried a `TENANT_ROLE_LEVELS` ladder (`viewer 0 … sysadmin 5`)
 * and compared the caller's role string against the `power_user` rung. Three
 * things were wrong with that and only one of them was visible:
 *
 *   - It was a fourth copy of a hierarchy the server does not have. The
 *     backend gate it claims to mirror is
 *     `requirePermissionOr403(req, reply, 'write', 'connections')`
 *     (routes/v1/connections-module.ts) — a grant, not a rung.
 *   - A tenant's OWN role has no rung, so a custom role that carried
 *     `write:connections` was denied by the browser and allowed by the API.
 *   - `power_user` did not exist as a real role until c24, so on the live box
 *     the rung resolved to admin-and-above anyway. Asking for the grant is
 *     therefore the same answer for every real user today, and the RIGHT
 *     answer for a tenant role tomorrow.
 *
 * The caller now passes the answer instead of the role: `canWriteConnections`
 * comes from `useHasPermission('write', 'connections')`, which is a lookup in
 * the grant set `GET /users/me` returned.
 */

export type RoleGateOutcome =
  | 'allow'
  | 'deny_role_too_low'
  | 'deny_sysadmin_context';

/**
 * Decide whether to render the Connections write surface.
 *
 * - sysadmin audience → deny with a redirect hint to /sysadmin/catalog
 *   (the tenant-scoped Connections write API would 401 anyway), and this is
 *   checked FIRST: a sysadmin token holds no tenant grant, so without it the
 *   operator would get the wrong "ask an admin" message.
 * - tenant audience without `write:connections` → deny with the placeholder.
 * - tenant audience with it → allow.
 */
export function decideConnectionsRoleGate(input: {
  contextKind: 'tenant' | 'sysadmin';
  canWriteConnections: boolean;
}): RoleGateOutcome {
  if (input.contextKind === 'sysadmin') return 'deny_sysadmin_context';
  // Nothing fails open: only an explicit `true` allows.
  if (input.canWriteConnections !== true) return 'deny_role_too_low';
  return 'allow';
}
